import { getCommunities } from '../../network/API'
import { getCustomersSelect } from '../../network/Customers'
import { normalizePhoneDigits, sanitizePhoneInput } from '../../utils/phoneNumber'

const hasPhoneNumber = (phoneNumber) =>
    normalizePhoneDigits(phoneNumber).length > 0

const hasCustomer = (customer) => {
    if (!customer) {
        return false
    }
    if (typeof customer === 'object' && 'value' in customer) {
        return Boolean(customer.value)
    }
    return Boolean(customer)
}

const clearPhoneWhenCustomerSelected = (form, name, e) => {
    if (!form) return
    const id = e?.value ?? e
    if (id) {
        form.setFieldsValue({ phoneNumber: undefined })
    }
}

const clearCustomerWhenPhoneEntered = (form, name, e) => {
    if (!form) return
    if (normalizePhoneDigits(e)) {
        form.setFieldsValue({ customer: undefined })
    }
}

const extractRelationId = (value) => {
    if (value == null) {
        return null
    }
    if (typeof value === 'object' && 'value' in value) {
        return value.value
    }
    return value
}

export const cleanGiftCardPayload = (oldData) => {
    delete oldData.grantedByName

    oldData.customer = extractRelationId(oldData.customer)
    if (!oldData.customer) {
        oldData.customer = null
    }

    oldData.offeredBy = extractRelationId(oldData.offeredBy)
    if (!oldData.offeredBy) {
        oldData.offeredBy = null
    }

    if (oldData.customer) {
        delete oldData.phoneNumber
    } else if (
        oldData.phoneNumber != null &&
        normalizePhoneDigits(oldData.phoneNumber)
    ) {
        oldData.phoneNumber = sanitizePhoneInput(oldData.phoneNumber)
    } else {
        oldData.phoneNumber = null
    }

    return oldData
}

const GiftCard = {
    title: 'gift_card_information',
    key: [],
    sections: [
        {
            columns: 6,
            fields: [
                {
                    label: 'GIFT_CARDS_GRANTED_BY',
                    isInactive: true,
                    key: ['grantedByName'],
                    pos: {
                        col: 1,
                        row: 1,
                        size: 1,
                    },
                },
                {
                    label: 'GIFT_CARDS_GIFT_CARD_ID',
                    isInactive: true,
                    key: ['giftCardID'],
                    pos: {
                        col: 2,
                        row: 1,
                        size: 1,
                    },
                },
                {
                    label: 'GIFT_CARDS_TYPE',
                    type: 'select',
                    isInactive: true,
                    options: [
                        { key: 'purchase', label: 'purchase' },
                        { key: 'gift', label: 'gift' },
                        { key: 'manual', label: 'manual' },
                        { key: 'other', label: 'other' },
                    ],
                    key: ['type'],
                    pos: {
                        col: 3,
                        row: 1,
                        size: 1,
                    },
                },
                {
                    label: 'CUSTOMERS_CUSTOMER_NAME',
                    type: 'select',
                    requestOption: getCustomersSelect,
                    remoteSearch: true,
                    searchKey: 'name',
                    key: ['customer'],
                    allowClear: true,
                    labelInValue: true,
                    inactiveWatchKey: ['phoneNumber'],
                    inactiveCondition: hasPhoneNumber,
                    onChange: clearPhoneWhenCustomerSelected,
                    pos: {
                        col: 1,
                        row: 2,
                        size: 1,
                    },
                },
                {
                    label: 'GIFT_CARDS_PHONE_NUMBER',
                    key: ['phoneNumber'],
                    inactiveWatchKey: ['customer'],
                    inactiveCondition: hasCustomer,
                    placeholder: 'GIFT_CARDS_PHONE_NUMBER_PLACEHOLDER',
                    translatePlaceholder: true,
                    onChange: clearCustomerWhenPhoneEntered,
                    pos: {
                        col: 2,
                        row: 2,
                        size: 1,
                    },
                },
                {
                    label: 'amount',
                    suffixLabel: '¥',
                    type: 'number',
                    isRequired: true,
                    key: ['amount'],
                    pos: {
                        col: 1,
                        row: 3,
                        size: 1,
                    },
                },
                {
                    label: 'ZIKO_SPECIALS_EXPIRATION_DATE',
                    type: 'date',
                    isRequired: true,
                    key: ['expirationDate'],
                    pos: {
                        col: 2,
                        row: 3,
                        size: 1,
                    },
                },
                {
                    label: 'GIFT_CARDS_OFFERED_BY',
                    type: 'select',
                    requestOption: getCustomersSelect,
                    remoteSearch: true,
                    searchKey: 'name',
                    key: ['offeredBy'],
                    allowClear: true,
                    pos: {
                        col: 3,
                        row: 3,
                        size: 1,
                    },
                },
                {
                    label: 'COMMUNITY_COMMUNITY',
                    key: ['communities'],
                    isRequired: true,
                    type: 'multicheck',
                    requestOption: getCommunities,
                    pos: {
                        col: 1,
                        row: 4,
                        size: 3,
                    },
                },
                {
                    key: ['grantedBy'],
                    type: 'hidden',
                },
                {
                    key: ['status'],
                    type: 'hidden',
                },
            ],
        },
    ],
}

export { GiftCard }

const GiftCardEdit = {
    title: 'gift_card_information',
    key: [],
    sections: [
        {
            columns: 6,
            fields: [
                {
                    label: 'GIFT_CARDS_GRANTED_BY',
                    isInactive: true,
                    key: ['grantedByName'],
                    pos: {
                        col: 1,
                        row: 1,
                        size: 1,
                    },
                },
                {
                    label: 'GIFT_CARDS_GIFT_CARD_ID',
                    isInactive: true,
                    key: ['giftCardID'],
                    pos: {
                        col: 2,
                        row: 1,
                        size: 1,
                    },
                },
                {
                    label: 'GIFT_CARDS_TYPE',
                    type: 'select',
                    isInactive: true,
                    options: [
                        { key: 'purchase', label: 'purchase' },
                        { key: 'gift', label: 'gift' },
                        { key: 'manual', label: 'manual' },
                        { key: 'other', label: 'other' },
                    ],
                    key: ['type'],
                    pos: {
                        col: 3,
                        row: 1,
                        size: 1,
                    },
                },
                {
                    label: 'CUSTOMERS_CUSTOMER_NAME',
                    type: 'select',
                    requestOption: getCustomersSelect,
                    remoteSearch: true,
                    searchKey: 'name',
                    key: ['customer'],
                    isRequired: false,
                    allowClear: true,
                    labelInValue: true,
                    inactiveWatchKey: ['phoneNumber'],
                    inactiveCondition: hasPhoneNumber,
                    onChange: clearPhoneWhenCustomerSelected,
                    pos: {
                        col: 1,
                        row: 2,
                        size: 1,
                    },
                },
                {
                    label: 'GIFT_CARDS_PHONE_NUMBER',
                    key: ['phoneNumber'],
                    inactiveWatchKey: ['customer'],
                    inactiveCondition: hasCustomer,
                    placeholder: 'GIFT_CARDS_PHONE_NUMBER_PLACEHOLDER',
                    translatePlaceholder: true,
                    onChange: clearCustomerWhenPhoneEntered,
                    pos: {
                        col: 2,
                        row: 2,
                        size: 1,
                    },
                },
                {
                    label: 'amount',
                    suffixLabel: '¥',
                    type: 'number',
                    isRequired: true,
                    key: ['amount'],
                    pos: {
                        col: 1,
                        row: 3,
                        size: 1,
                    },
                },
                {
                    label: 'ZIKO_SPECIALS_EXPIRATION_DATE',
                    type: 'date',
                    key: ['expirationDate'],
                    pos: {
                        col: 2,
                        row: 3,
                        size: 1,
                    },
                },
                {
                    label: 'GIFT_CARDS_OFFERED_BY',
                    type: 'select',
                    requestOption: getCustomersSelect,
                    remoteSearch: true,
                    searchKey: 'name',
                    key: ['offeredBy'],
                    isRequired: false,
                    allowClear: true,
                    labelInValue: true,
                    pos: {
                        col: 3,
                        row: 3,
                        size: 1,
                    },
                },
                {
                    label: 'COMMUNITY_COMMUNITY',
                    key: ['communities'],
                    isRequired: true,
                    type: 'multicheck',
                    requestOption: getCommunities,
                    pos: {
                        col: 1,
                        row: 4,
                        size: 3,
                    },
                },
                {
                    key: ['grantedBy'],
                    type: 'hidden',
                },
                {
                    key: ['status'],
                    type: 'hidden',
                },
            ],
        },
    ],
}

export { GiftCardEdit, hasPhoneNumber, hasCustomer }
